import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import GoBackButton from '../../components/GoBackButton';
import FooterSection from '../landing-page/components/FooterSection';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';

const ContactUs = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { t } = useLanguage();
  const language = t('lang') === 'bn' ? 'bn' : 'en';

  const subjects = [
    t('generalEnquiry'),
    language === 'bn' ? 'সম্পত্তি বিশ্লেষণ সহায়তা' : 'Property Analysis Support',
    language === 'bn' ? 'তালিকা সহায়তা' : 'Listing Help',
    language === 'bn' ? 'পেশাদার মার্কেটপ্লেস' : 'Professional Marketplace',
    t('billingSupport'),
    t('technicalIssue'),
    language === 'bn' ? 'গোপনীয়তা ও ডেটা অনুরোধ' : 'Privacy & Data Request',
    language === 'bn' ? 'অংশীদারিত্বের সুযোগ' : 'Partnership Opportunity',
    language === 'bn' ? 'অন্যান্য' : 'Other',
  ];

  const contactInfo = [
    {
      icon: 'Mail',
      label: 'General Support',
      value: 'support@listinglens.live',
      href: 'mailto:support@listinglens.live',
    },
    {
      icon: 'Shield',
      label: 'Privacy Officer',
      value: 'privacy@listinglens.live',
      href: 'mailto:privacy@listinglens.live',
    },
    {
      icon: 'FileText',
      label: 'Legal Enquiries',
      value: 'legal@listinglens.live',
      href: 'mailto:legal@listinglens.live',
    },
    {
      icon: 'Clock',
      label: 'Business Hours',
      value: 'Mon–Fri, 9am–6pm AEST',
      href: null,
    },
  ];

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e?.target?.name]: e?.target?.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name || !form?.email || !form?.subject || !form?.message) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { error: dbError } = await supabase?.from('contact_submissions')?.insert([{
          name: form?.name,
          email: form?.email,
          phone: form?.phone || null,
          subject: form?.subject,
          message: form?.message,
        }]);
      if (dbError) throw dbError;
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setError('Failed to send message. Please try again or email us directly at support@listinglens.live');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#050A14' }}>
      {/* Nav */}
      <nav className="border-b border-border/30 px-4 md:px-8 h-16 flex items-center justify-between" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(12px)' }}>
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/assets/images/logo_LL-1772022913346.jpeg"
            alt="ListingLens logo"
            className="h-9 w-auto rounded-lg object-cover"
            style={{ boxShadow: '0 0 12px rgba(0,212,255,0.4)', border: '1px solid rgba(0,212,255,0.3)', maxWidth: '130px' }}
          />
        </Link>
        <GoBackButton fallbackPath="/" label="Go Back" />
      </nav>
      {/* Hero */}
      <section className="py-16 md:py-24 px-4 md:px-8 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.2)' }}>
            <Icon name="MessageCircle" size={14} />
            {t('contactUs')}
          </div>
          <h1 className="text-4xl md:text-5xl font-black mb-4" style={{ color: '#F0F4FF' }}>{t('contactTitle')}</h1>
          <p className="text-lg" style={{ color: 'rgba(240,244,255,0.6)' }}>{t('contactSubtitle')}. {language === 'bn' ? 'আমাদের একটি বার্তা পাঠান এবং আমরা ১ কার্যদিবসের মধ্যে সাড়া দেব।' : "Have a question or need help? We'd love to hear from you. Send us a message and we'll respond within 1 business day."}</p>
        </div>
      </section>
      <div className="max-w-6xl mx-auto px-4 md:px-8 pb-20">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Contact Info */}
          <div className="space-y-6">
            <h2 className="text-xl font-bold" style={{ color: '#F0F4FF' }}>{t('contactInformation')}</h2>
            {contactInfo?.map((info) => (
              <div key={info?.label} className="flex items-start gap-4 p-5 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(0,212,255,0.1)' }}>
                  <Icon name={info?.icon} size={18} color="#00D4FF" />
                </div>
                <div>
                  <p className="text-xs font-medium mb-1" style={{ color: 'rgba(240,244,255,0.5)' }}>{info?.label}</p>
                  {info?.href ? (
                    <a href={info?.href} className="text-sm font-medium hover:text-primary transition-colors" style={{ color: '#F0F4FF' }}>{info?.value}</a>
                  ) : (
                    <p className="text-sm font-medium" style={{ color: '#F0F4FF' }}>{info?.value}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Quick Links */}
            <div className="p-5 rounded-xl" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)' }}>
              <h3 className="font-semibold mb-3" style={{ color: '#F0F4FF' }}>{t('quickHelp')}</h3>
              <div className="space-y-2">
                <Link to="/faq" className="flex items-center gap-2 text-sm hover:text-primary transition-colors" style={{ color: 'rgba(240,244,255,0.7)' }}>
                  <Icon name="HelpCircle" size={14} color="#00D4FF" />
                  {language === 'bn' ? 'প্রশ্নাবলী দেখুন' : 'Browse FAQ'}
                </Link>
                <Link to="/help-support" className="flex items-center gap-2 text-sm hover:text-primary transition-colors" style={{ color: 'rgba(240,244,255,0.7)' }}>
                  <Icon name="BookOpen" size={14} color="#00D4FF" />
                  {language === 'bn' ? 'সহায়তা ও গাইড' : 'Help & Support Guides'}
                </Link>
                <Link to="/privacy-policy" className="flex items-center gap-2 text-sm hover:text-primary transition-colors" style={{ color: 'rgba(240,244,255,0.7)' }}>
                  <Icon name="Shield" size={14} color="#00D4FF" />
                  {t('privacyPolicy')}
                </Link>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            {success ? (
              <div className="p-10 rounded-2xl text-center" style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.2)' }}>
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(0,212,255,0.15)' }}>
                  <Icon name="CheckCircle" size={32} color="#00D4FF" />
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#F0F4FF' }}>{t('messageSentTitle')}</h3>
                <p className="mb-6" style={{ color: 'rgba(240,244,255,0.6)' }}>{t('messageSentDesc')}</p>
                <button
                  onClick={() => setSuccess(false)}
                  className="px-6 py-3 rounded-xl font-medium text-sm transition-all"
                  style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}
                >
                  {t('sendAnotherMessage')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-8 rounded-2xl space-y-6" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <h2 className="text-xl font-bold" style={{ color: '#F0F4FF' }}>{t('sendUsMessage')}</h2>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(240,244,255,0.7)' }}>{t('fullName')} <span style={{ color: '#00D4FF' }}>*</span></label>
                    <input
                      type="text"
                      name="name"
                      value={form?.name}
                      onChange={handleChange}
                      placeholder={t('yourFullName')}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F4FF' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(240,244,255,0.7)' }}>{t('email')} <span style={{ color: '#00D4FF' }}>*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={form?.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F4FF' }}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(240,244,255,0.7)' }}>{t('phone')} ({t('optional')})</label>
                    <input
                      type="tel"
                      name="phone"
                      value={form?.phone}
                      onChange={handleChange}
                      placeholder="+61 or +971..."
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F4FF' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(240,244,255,0.7)' }}>{t('subject')} <span style={{ color: '#00D4FF' }}>*</span></label>
                    <select
                      name="subject"
                      value={form?.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: form?.subject ? '#F0F4FF' : 'rgba(240,244,255,0.4)' }}
                    >
                      <option value="" style={{ background: '#0D1B2A' }}>{t('selectSubject')}</option>
                      {subjects?.map((s) => (
                        <option key={s} value={s} style={{ background: '#0D1B2A', color: '#F0F4FF' }}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'rgba(240,244,255,0.7)' }}>{t('message')} <span style={{ color: '#00D4FF' }}>*</span></label>
                  <textarea
                    name="message"
                    value={form?.message}
                    onChange={handleChange}
                    rows={6}
                    placeholder={t('tellUsHowHelp')}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all resize-none"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#F0F4FF' }}
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.2)' }}>
                    <Icon name="AlertCircle" size={16} color="#FF3B30" />
                    <p className="text-sm" style={{ color: '#FF3B30' }}>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 rounded-xl font-bold text-base transition-all flex items-center justify-center gap-2"
                  style={{ background: loading ? 'rgba(0,212,255,0.3)' : 'linear-gradient(135deg, #00D4FF, #7B2FFF)', color: '#fff', cursor: loading ? 'not-allowed' : 'pointer' }}
                >
                  {loading ? (
                    <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t('sending')}</>
                  ) : (
                    <><Icon name="Send" size={18} />{t('sendMessage')}</>
                  )}
                </button>

                <p className="text-xs text-center" style={{ color: 'rgba(240,244,255,0.4)' }}>
                  By submitting this form, you agree to our{' '}
                  <Link to="/privacy-policy" className="hover:text-primary transition-colors" style={{ color: '#00D4FF' }}>Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
      <FooterSection />
    </div>
  );
};

export default ContactUs;
